// Remote Relay's browser storage (docs/remote-relay.md): this browser's relay
// identity key, its connections, cached task history and unconfirmed
// requests (the outbox).
//
// As with the app's other data (db.ts), there is one database per Changeling
// user (`remote-relay:<user ID>`), so one browser profile never shows or
// uses another user's agents, and it is deleted at sign-out, identity
// included. Both the page (relay.ts) and the relay worker open it. Each
// connection closes itself when the database is deleted, so neither an idle
// tab nor the worker holds a deletion up.
const PREFIX = 'remote-relay';
/** The single database every user shared before it was per user. */
const LEGACY = 'remote-relay-v1';

export function relayDbName(user: string): string {
  return `${PREFIX}:${user}`;
}

const open = new Map<string, Promise<IDBDatabase>>();
const closedListeners = new Set<(user: string) => void>();

/** Calls `listener` with the user whose database another context is deleting. */
export function onDatabaseClosed(listener: (user: string) => void): () => void {
  closedListeners.add(listener);
  return () => closedListeners.delete(listener);
}

function db(user: string): Promise<IDBDatabase> {
  let database = open.get(user);
  if (!database) {
    database = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(relayDbName(user), 1);
      request.onupgradeneeded = () => request.result.createObjectStore('values');
      request.onsuccess = () => {
        const result = request.result;
        result.onversionchange = () => {
          result.close();
          if (open.get(user) === database) open.delete(user);
          for (const listener of closedListeners) listener(user);
        };
        resolve(result);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Close other Changeling tabs to update device storage'));
    });
    const opened = database;
    opened.catch(() => { if (open.get(user) === opened) open.delete(user); });
    open.set(user, opened);
  }
  return database;
}

export async function read<T>(user: string, key: string): Promise<T | undefined> {
  const database = await db(user);
  return new Promise((resolve, reject) => {
    const request = database.transaction('values').objectStore('values').get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

export async function write(user: string, key: string, value: unknown): Promise<void> {
  const database = await db(user);
  return new Promise<void>((resolve, reject) => {
    const tx = database.transaction('values', 'readwrite');
    if (value === undefined) tx.objectStore('values').delete(key); else tx.objectStore('values').put(value, key);
    tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
  });
}

/** The value at `key`, or `make()` stored there when there is none, in one
 *  transaction: two contexts asking at once get the same value. */
export async function readOrCreate<T>(user: string, key: string, make: () => T): Promise<T> {
  const database = await db(user);
  return new Promise<T>((resolve, reject) => {
    const tx = database.transaction('values', 'readwrite');
    const store = tx.objectStore('values');
    let value: T;
    const request = store.get(key);
    request.onsuccess = () => {
      if (request.result !== undefined) value = request.result as T;
      else { value = make(); store.put(value, key); }
    };
    tx.oncomplete = () => resolve(value); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
  });
}

export async function removePrefix(user: string, prefix: string): Promise<void> {
  const database = await db(user);
  return new Promise<void>((resolve, reject) => {
    const tx = database.transaction('values', 'readwrite');
    const request = tx.objectStore('values').openCursor();
    request.onsuccess = () => { const cursor = request.result; if (cursor) { if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) cursor.delete(); cursor.continue(); } };
    tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
  });
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/** Deletes `user`'s relay database: identity, connections, history and outbox. */
export async function deleteRelayData(user: string): Promise<void> {
  const database = open.get(user);
  open.delete(user);
  (await database?.catch(() => null))?.close();
  await deleteDatabase(relayDbName(user));
}

/** Deletes every user's relay database but `keep`'s (all of them when `keep`
 *  is null), and the database from before they were per user. */
export async function deleteOtherRelayData(keep: string | null): Promise<void> {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return;
  const names = (await indexedDB.databases()).map(d => d.name)
    .filter((n): n is string => !!n && (n === LEGACY || n.startsWith(PREFIX + ':')))
    .filter(n => keep === null || n !== relayDbName(keep));
  await Promise.all(names.map(async n => {
    const user = n.slice(PREFIX.length + 1);
    if (n !== LEGACY && open.has(user)) await deleteRelayData(user);
    else await deleteDatabase(n);
  }));
}
